import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useApi } from '../services/api';
import { useCity } from '../contexts/CityContext';
import styles from './BusLineDetailPage.module.css';
import LineChip from '../components/common/LineChip';
import { MdDirectionsBoat } from 'react-icons/md';
import { processTimetables } from '../utils/transportUtils';

// Vapur hattı için interface tanımı
interface FerryLine {
  ferry_line_id: string;
  ferry_line_name: string;
  ferry_line_path_description: string;
  description?: string;
  start_ferry_stop?: string;
  end_ferry_stop?: string;
  ferry_line_color?: string;
  active: boolean;
  operator?: {
    operator_id: number;
    operator_name: string;
    operator_logo?: string;
    operator_website?: string;
    operator_phone?: string;
  };
  stops?: {
    stop_id: string;
    stop_name: string;
    order: number;
    latitude?: number;
    longitude?: number;
  }[];
  live_vehicles?: any[];
  ferry_line_announcements?: any[];
  timetables?: any[];
  schedule?: {
    weekday: string[];
    saturday: string[];
    sunday: string[];
  };
  fare?: {
    regular: number;
    student?: number;
    senior?: number;
    handicapped?: number;
  };
  features?: {
    accessibility: boolean;
    wifi: boolean;
    restaurant: boolean;
  };
  path_coordinates?: [number, number][];
  paths?: {
    direction: 'forward' | 'backward' | 'circular';
    encoded_polyline: string;
    decoded_path?: [number, number][];
    color?: string;
    stops?: string[];
  }[];
  duration?: number;
  distance?: number;
  frequency?: number;
}

function FerryLineDetailPage() {
  const { lineId } = useParams<{ lineId: string }>();
  const [lineDetail, setLineDetail] = useState<FerryLine | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'details' | 'stops' | 'schedule' | 'map'>('details');
  const api = useApi();
  const { currentCity } = useCity();
  const navigate = useNavigate();

  const transportMode = 'ferry';

  const getModeIcon = () => <MdDirectionsBoat />;
  const getModeTitle = () => 'Vapur';

  const fetchLineDetail = useCallback(async () => {
    if (!lineId || !currentCity) return;

    setLoading(true);
    setError(null);

    try {
      const response = await api.getTransportLine(transportMode, lineId);

      if (response && response.status === 'success' && response.data) {
        const lineData = response.data;

        if (lineData.timetables && lineData.timetables.length > 0) {
          lineData.schedule = processTimetables(lineData.timetables);
        }

        setLineDetail(lineData);
      } else {
        setError('Vapur hattı bilgisi bulunamadı.');
      }
    } catch (err) {
      setError('Hattı yüklerken bir hata oluştu.');
    } finally {
      setLoading(false);
    }
  }, [lineId, currentCity, api]);

  useEffect(() => {
    fetchLineDetail();
  }, [fetchLineDetail]);

  return (
    <div className={styles.lineDetailPage}>
      <div className={styles.backButtonContainer}>
        <button className={styles.backButton} onClick={() => navigate('/lines')}>
          ← Hatlara Dön
        </button>
      </div>

      {loading && <div className={styles.loadingMessage}>Yükleniyor...</div>}

      {error && <div className={styles.errorMessage}>{error}</div>}

      {!loading && !error && lineDetail && (
        <>
          <div className={styles.lineHeader}>
            <div className={styles.modeIndicator}>
              {getModeIcon()}
              <span>{getModeTitle()}</span>
            </div>
            <LineChip lineNumber={lineDetail.ferry_line_id} size="large" lineColor={lineDetail.ferry_line_color || undefined} />
            <h1 className={styles.lineName}>{lineDetail.ferry_line_name}</h1>
          </div>

          <div className={styles.tabMenu}>
            <button
              className={`${styles.tabButton} ${activeTab === 'details' ? styles.activeTab : ''}`}
              onClick={() => setActiveTab('details')}
            >
              Detaylar
            </button>
            <button
              className={`${styles.tabButton} ${activeTab === 'stops' ? styles.activeTab : ''}`}
              onClick={() => setActiveTab('stops')}
            >
              Duraklar
            </button>
            <button
              className={`${styles.tabButton} ${activeTab === 'schedule' ? styles.activeTab : ''}`}
              onClick={() => setActiveTab('schedule')}
            >
              Tarife
            </button>
            <button
              className={`${styles.tabButton} ${activeTab === 'map' ? styles.activeTab : ''}`}
              onClick={() => setActiveTab('map')}
            >
              Harita
            </button>
          </div>

          {activeTab === 'details' && (
            <div className={styles.lineDetailsCard}>
              <h3>Güzergah Açıklaması</h3>
              <p>{lineDetail.ferry_line_path_description}</p>
            </div>
          )}

          {activeTab === 'stops' && (
            <div className={styles.stopsCard}>
              <h3>Duraklar</h3>
              {lineDetail.stops && lineDetail.stops.length > 0 ? (
                <ul>
                  {lineDetail.stops.map((stop) => (
                    <li key={stop.stop_id}>{stop.stop_name}</li>
                  ))}
                </ul>
              ) : (
                <p>Bu hat için durak bilgisi bulunamadı.</p>
              )}
            </div>
          )}

          {activeTab === 'schedule' && (
            <div className={styles.scheduleCard}>
              <h3>Sefer Saatleri</h3>
              {lineDetail.schedule && lineDetail.schedule.weekday.length > 0 ? (
                <ul>
                  {lineDetail.schedule.weekday.map((time, index) => (
                    <li key={index}>{time}</li>
                  ))}
                </ul>
              ) : (
                <p>Bu hat için sefer saati bilgisi bulunamadı.</p>
              )}
            </div>
          )}

          {activeTab === 'map' && <div className={styles.mapCard}>Harita</div>}
        </>
      )}
    </div>
  );
}

export default FerryLineDetailPage;